'use client'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@repo/ui/components/accordion'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/components/sheet'
import type { PortableTextBlock } from 'next-sanity'
import { FunctionComponent, ReactNode } from 'react'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { PageDto } from '../../../types/page.types'
import { ProductDto } from '../../../types/product.types'
import PortableText from '../../text/PortableText'
import SizeGuideTable from './SizeGuideTable'
import SizeGuideUnitsTables from './SizeGuideUnitsTables'

interface Props {
  pageDto: PageDto
  productDto: ProductDto
}

const ProductSizeGuideSheet: FunctionComponent<Props> = ({ pageDto, productDto }) => {
  const { isSheetOpen, closeSheet } = useSheet()
  const sizeGuide = productDto.sanityProduct?.sizeGuide

  const items: { _key: string; title: string; content: ReactNode; className?: string }[] = [
    ...(sizeGuide?.conversionTable?.table
      ? [
          {
            _key: 'conversion',
            title: sizeGuide.conversionTable.title || 'International Size Converter',
            content: <SizeGuideTable table={sizeGuide.conversionTable.table} />,
            className: 'px-0',
          },
        ]
      : []),
    ...(sizeGuide?.tables
      ? [
          {
            _key: 'measurements',
            title: sizeGuide.tablesTitle || 'Measurements',
            content: <SizeGuideUnitsTables tables={sizeGuide?.tables} />,
            className: 'px-0',
          },
        ]
      : []),
    ...(sizeGuide?.measurementInstruction?.instructions
      ? [
          {
            _key: 'instructions',
            title: sizeGuide.measurementInstruction.title || 'How to Measure',
            content: <PortableText value={sizeGuide?.measurementInstruction.instructions as PortableTextBlock[]} locale={pageDto.params.locale} imageSizes='32rem' />,
          },
        ]
      : []),
  ]

  if (items.length === 0) {
    return null
  }

  return (
    <Sheet open={isSheetOpen(SHEET.SIZING)} onOpenChange={(open) => !open && closeSheet()}>
      <SheetContent side='right'>
        <SheetHeader className='border-0'>
          <SheetTitle>{productDto.sanityProduct?.sizeGuide?.title || pageDto.dictionary?.sizeguide?.sizeguide || 'Size Guide'}</SheetTitle>
        </SheetHeader>
        <SheetDescription className='sr-only'>{pageDto.dictionary?.sizeguide?.sizeguideandinstructions || 'Size guide and measurement instructions.'}</SheetDescription>
        <div className='max-h-[90svh] w-full overflow-y-auto overflow-x-hidden'>
          <Accordion className='w-full' type='multiple' defaultValue={items.map((item) => item._key)}>
            {items?.map((item) => (
              <AccordionItem key={item._key} value={item._key} variant='lightStatic' rounded={false}>
                <AccordionTrigger>{item.title}</AccordionTrigger>
                <AccordionContent className={item.className}>{item.content}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default ProductSizeGuideSheet
